import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, Select } from "../../shared/ui";
import type {
  EmployeeSummary,
  PageResponse,
  TeamMemberView,
  TeamSummary,
} from "../../shared/types/api";

const today = () => new Date().toISOString().slice(0, 10);

const PROJECT_ROLES = [
  "PROJECT_MANAGER",
  "TEAM_LEAD",
  "DEVELOPER",
  "QA",
  "DESIGNER",
  "DEVOPS",
  "BA",
  "STAKEHOLDER",
];

/** Turns a server rejection into something the reader can act on. */
function describe(error: unknown): string {
  if (error instanceof ApiError && error.code === "MEMBERSHIP_OVERLAP") {
    return (
      "That employee already has an overlapping membership of this team. " +
      "End the existing period first, or choose a later start date."
    );
  }
  if (error instanceof ApiError) {
    return error.message;
  }
  return "Something went wrong. Try again.";
}

const addSchema = z.object({
  employeeId: z.string().min(1, "Choose an employee"),
  role: z.string().min(1, "Choose a role"),
  effectiveFrom: z.string().min(1, "Choose a start date"),
  membershipPercentage: z.coerce.number().min(1, "At least 1%").max(100, "At most 100%"),
});

/**
 * Adds someone to a team from a date.
 *
 * <p>There is deliberately no end-date field. A membership is opened here and closed through its
 * own action; offering both at once invites people to express a move as a single edit, which is
 * precisely the overwrite the temporal model exists to prevent.
 */
export function AddMemberDrawer({
  teamId,
  teamName,
  open,
  onClose,
  onSaved,
}: {
  teamId: string;
  teamName: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof addSchema>>({
    resolver: zodResolver(addSchema),
    defaultValues: { role: "DEVELOPER", effectiveFrom: today(), membershipPercentage: 100 },
  });

  const close = () => {
    reset();
    setFormError(null);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.post(`/api/v1/teams/${teamId}/memberships`, values);
      onSaved();
      close();
    } catch (error) {
      setFormError(describe(error));
    }
  });

  return (
    <Drawer
      open={open}
      onClose={close}
      title="Add a member"
      subtitle={teamName}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void onSubmit()} loading={isSubmitting}>
            {isSubmitting ? "Adding…" : "Add member"}
          </Button>
        </div>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        <Field label="Employee" required error={errors.employeeId?.message}>
          <Select {...register("employeeId")} disabled={employees.isPending}>
            <option value="">
              {employees.isPending ? "Loading…" : "Choose an employee"}
            </option>
            {(employees.data?.content ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName} ({employee.employeeCode})
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Role in this team" required error={errors.role?.message}>
          <Select {...register("role")}>
            {PROJECT_ROLES.map((role) => (
              <option key={role} value={role}>
                {role.replace(/_/g, " ")}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Member from"
          required
          error={errors.effectiveFrom?.message}
          hint="Membership is recorded from this date onwards, and stays in the record afterwards."
        >
          <Input type="date" {...register("effectiveFrom")} />
        </Field>

        <Field
          label="Allocation %"
          required
          error={errors.membershipPercentage?.message}
          hint="How much of their time this team represents. Someone on two teams might be 60 and 40."
        >
          <Input type="number" min={1} max={100} {...register("membershipPercentage")} />
        </Field>
      </form>
    </Drawer>
  );
}

/** Ends a membership on a date, keeping the record. */
export function CloseMembershipDrawer({
  teamId,
  member,
  onClose,
  onSaved,
}: {
  teamId: string;
  member: TeamMemberView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [effectiveTo, setEffectiveTo] = useState(today);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!member) {
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await api.post(`/api/v1/teams/${teamId}/memberships/${member.membershipId}/close`, {
        effectiveTo,
      });
      onSaved();
      onClose();
    } catch (error) {
      setFormError(describe(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(member)}
      onClose={onClose}
      title="End membership"
      subtitle={member?.fullName}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void submit()} loading={saving}>
            {saving ? "Ending…" : "End membership"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        <InlineAlert tone="info">
          The record of their time in this team is kept, so past reports stay correct. Nothing is
          deleted.
        </InlineAlert>

        <Field
          label="Membership ends on"
          required
          hint="They are a member up to but not including this date."
        >
          <Input
            type="date"
            value={effectiveTo}
            onChange={(event) => setEffectiveTo(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}

const moveSchema = z.object({
  toTeamId: z.string().min(1, "Choose a destination team"),
  role: z.string().min(1, "Choose a role"),
  effectiveFrom: z.string().min(1, "Choose a date"),
  membershipPercentage: z.coerce.number().min(1).max(100),
});

/**
 * Moves someone to another team on one date.
 *
 * <p>Sent as a single request so the two halves cannot come apart. Done as two separate actions, a
 * failure between them would leave the person in both teams or in neither, and nothing would
 * report it.
 */
export function MoveMemberDrawer({
  fromTeamId,
  member,
  onClose,
  onSaved,
}: {
  fromTeamId: string;
  member: TeamMemberView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);

  const teams = useQuery({
    queryKey: ["teams", "picker"],
    queryFn: () => api.get<PageResponse<TeamSummary>>("/api/v1/teams?size=200&sort=name,asc"),
    enabled: Boolean(member),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof moveSchema>>({
    resolver: zodResolver(moveSchema),
    defaultValues: { role: "DEVELOPER", effectiveFrom: today(), membershipPercentage: 100 },
  });

  const close = () => {
    reset();
    setFormError(null);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!member) {
      return;
    }
    setFormError(null);
    try {
      await api.post(`/api/v1/teams/${fromTeamId}/memberships/move`, {
        employeeId: member.employeeId,
        ...values,
      });
      onSaved();
      close();
    } catch (error) {
      setFormError(describe(error));
    }
  });

  return (
    <Drawer
      open={Boolean(member)}
      onClose={close}
      title="Move to another team"
      subtitle={member?.fullName}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void onSubmit()} loading={isSubmitting}>
            {isSubmitting ? "Moving…" : "Move"}
          </Button>
        </div>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        <InlineAlert tone="info">
          Their membership of this team ends on the date below and the new one begins the same day.
          Both periods stay in the record.
        </InlineAlert>

        <Field label="Move to" required error={errors.toTeamId?.message}>
          <Select {...register("toTeamId")} disabled={teams.isPending}>
            <option value="">{teams.isPending ? "Loading…" : "Choose a team"}</option>
            {(teams.data?.content ?? [])
              .filter((team) => team.id !== fromTeamId)
              .map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name} ({team.code})
                </option>
              ))}
          </Select>
        </Field>

        <Field label="Role in the new team" required error={errors.role?.message}>
          <Select {...register("role")}>
            {PROJECT_ROLES.map((role) => (
              <option key={role} value={role}>
                {role.replace(/_/g, " ")}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Effective from" required error={errors.effectiveFrom?.message}>
          <Input type="date" {...register("effectiveFrom")} />
        </Field>

        <Field label="Allocation %" required error={errors.membershipPercentage?.message}>
          <Input type="number" min={1} max={100} {...register("membershipPercentage")} />
        </Field>
      </form>
    </Drawer>
  );
}
